import React from 'react';
import { Alert } from '@mui/material';
import { IconCalendarRepeat } from '@tabler/icons-react';
import { Chip } from '../chip';
import type { FollowUpProblem, ResolvedFollowUp } from '../../utils/followUpTodos';
import './FollowUpTodosSection.css';

/**
 * The ticket form's preview of the to-dos its template schedules — each one
 * raises a ticket of its own on its date (e.g. removing a Conditional Access
 * country the day after a trip ends). Shows what that ticket will charge and
 * lets the tech choose No charge per line; the choice is saved on the to-do.
 * Presentational: TicketEntryPage resolves the follow-ups and owns the state.
 */

interface FollowUpTodosSectionProps {
  followUps: ResolvedFollowUp[];
  problems: FollowUpProblem[];
  disabled?: boolean;
  onNoChargeChange: (followUpIndex: number, lineIndex: number, noCharge: boolean) => void;
}

const formatDate = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

const money = (n: number) => `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

export const FollowUpTodosSection: React.FC<FollowUpTodosSectionProps> = ({
  followUps,
  problems,
  disabled = false,
  onNoChargeChange,
}) => {
  if (followUps.length === 0 && problems.length === 0) return null;

  return (
    <div className="follow-up-section">
      <div className="follow-up-section__title">
        <IconCalendarRepeat size={16} stroke={1.5} />
        Follow-up
      </div>

      {problems.map((p) =>
        p.kind === 'missing-date' ? (
          <div key={`problem-${p.index}`} className="follow-up-section__hint">
            {p.message}
          </div>
        ) : (
          <Alert key={`problem-${p.index}`} severity="warning">
            {p.message}
          </Alert>
        ),
      )}

      {followUps.map(({ index, target, request }) => (
        <div key={`follow-up-${index}`} className="follow-up-section__item">
          <div className="follow-up-section__head">
            <div className="follow-up-section__name">{request.title}</div>
            <div className="follow-up-section__meta">
              Raises <strong>{target.displayName}</strong> on {formatDate(request.scheduledDate)}
            </div>
          </div>

          {request.ticketPayload.lineItems.length === 0 ? (
            <div className="follow-up-section__empty">No charge lines</div>
          ) : (
            request.ticketPayload.lineItems.map((line, lineIndex) => {
              const total = line.nonChargeable ? 0 : line.quantity * line.unitCost;
              return (
                <div key={`${index}-${lineIndex}`} className="follow-up-section__line">
                  <span className="follow-up-section__sku">{line.sku}</span>
                  <span className="follow-up-section__qty">
                    {line.quantity} × {money(line.unitCost)}
                  </span>
                  <span className="follow-up-section__total">
                    {line.nonChargeable ? <Chip tone="neutral">No charge</Chip> : money(total)}
                  </span>
                  <label className="follow-up-section__toggle">
                    <input
                      type="checkbox"
                      checked={line.nonChargeable}
                      disabled={disabled}
                      onChange={(e) => onNoChargeChange(index, lineIndex, e.target.checked)}
                    />
                    No charge
                  </label>
                </div>
              );
            })
          )}
        </div>
      ))}
    </div>
  );
};
